import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

export interface Retrospective {
  id: string;
  company_id: string;
  team_id: string | null;
  sprint_id: string | null;
  retro_type: string;
  title: string | null;
  status: string;
  retro_date: string;
  created_by: string;
  created_by_name: string;
  team_name: string | null;
  sprint_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RetrospectiveItem {
  id: string;
  retrospective_id: string;
  section_key: string;
  content: string;
  added_by: string;
  added_by_name: string;
  sort_order: number;
}

export interface RetrospectiveAttendee {
  id: string;
  retrospective_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface RetrospectiveActionItem {
  id: string;
  retrospective_id: string;
  company_id: string;
  team_id: string | null;
  content: string;
  assignee_id: string | null;
  assignee_name: string | null;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: string | null;
  created_by: string;
  created_by_name: string;
  sort_order: number;
  created_at: string;
}

export interface RetrospectiveFull extends Retrospective {
  items: RetrospectiveItem[];
  attendees: RetrospectiveAttendee[];
  action_items: RetrospectiveActionItem[];
}

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function listRetrospectives(token: string, teamId?: string): Promise<Retrospective[]> {
  const params = teamId ? `?team_id=${teamId}` : '';
  const response = await axios.get(`${API_BASE_URL}/retrospectives${params}`, authHeaders(token));
  return response.data.data;
}

export async function getRetrospective(id: string, token: string): Promise<RetrospectiveFull> {
  const response = await axios.get(`${API_BASE_URL}/retrospectives/${id}`, authHeaders(token));
  return response.data.data;
}

export async function createRetrospective(data: {
  team_id?: string | null;
  sprint_id?: string | null;
  retro_type: string;
  title?: string | null;
  status?: string;
  retro_date?: string;
  items?: { section_key: string; content: string; sort_order?: number }[];
  attendee_ids?: string[];
  action_items?: { content: string; assignee_id?: string | null; sort_order?: number }[];
}, token: string): Promise<RetrospectiveFull> {
  const response = await axios.post(`${API_BASE_URL}/retrospectives`, data, authHeaders(token));
  return response.data.data;
}

export async function updateRetrospective(id: string, data: {
  team_id?: string | null;
  sprint_id?: string | null;
  title?: string | null;
  status?: string;
  retro_date?: string;
  items?: { id?: string; section_key: string; content: string; sort_order?: number }[];
  attendee_ids?: string[];
  action_items?: { id?: string; content: string; assignee_id?: string | null; sort_order?: number }[];
}, token: string): Promise<RetrospectiveFull> {
  const response = await axios.put(`${API_BASE_URL}/retrospectives/${id}`, data, authHeaders(token));
  return response.data.data;
}

export async function deleteRetrospective(id: string, token: string): Promise<void> {
  await axios.delete(`${API_BASE_URL}/retrospectives/${id}`, authHeaders(token));
}

export async function getOpenActionItems(teamId: string, token: string): Promise<RetrospectiveActionItem[]> {
  const response = await axios.get(`${API_BASE_URL}/retrospectives/action-items/open?team_id=${teamId}`, authHeaders(token));
  return response.data.data;
}

export async function toggleActionItem(id: string, token: string): Promise<RetrospectiveActionItem> {
  const response = await axios.put(`${API_BASE_URL}/retrospectives/action-items/${id}/toggle`, {}, authHeaders(token));
  return response.data.data;
}
